import { useMemo } from "react";
import type { ChartColorToken } from "@salestrak/ui";
import { useScopedWhere } from "#app/widgets/dashboards/scope/use-scoped-where";
import { ARCHIVED_OUT } from "#app/widgets/dashboards/use-dimension-slices";
import { useIssueGroupCounts } from "#app/hooks/issue-list/use-issue-group-counts";
import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import { ISSUE_AGE_STATUSES, type IssueAgeStatus } from "#app/lib/data";
import { AGE_STATUS_CONFIG } from "#app/lib/age-status";

/** One stacked bar: a stage (in pipeline order) and its count split by age health.
 *  `untracked` catches work with no age status (workspaces with age tracking off,
 *  or issues in a type that doesn't track age) so the pipeline never renders empty. */
export type ThroughputRow = {
  id: string;
  label: string;
  onTrack: number;
  atRisk: number;
  offTrack: number;
  untracked: number;
  total: number;
};

/** A stacked series key — an age-health bucket, or the age-less "untracked" one. */
export type ThroughputSeriesKey = IssueAgeStatus | "untracked";

/** The stacked series, in fixed order, each keyed to its semantic token. `untracked`
 *  is muted (it's the absence of age health, not a health state). */
export const THROUGHPUT_SERIES: {
  key: ThroughputSeriesKey;
  label: string;
  colorToken: ChartColorToken;
}[] = [
  { key: "onTrack", label: AGE_STATUS_CONFIG.onTrack.label, colorToken: "success" },
  { key: "atRisk", label: AGE_STATUS_CONFIG.atRisk.label, colorToken: "warning" },
  { key: "offTrack", label: AGE_STATUS_CONFIG.offTrack.label, colorToken: "destructive" },
  { key: "untracked", label: "Untracked", colorToken: "muted" },
];

/**
 * Per-stage project counts split by age health (On track / At risk / Off track),
 * ordered along the pipeline. The categorical `useIssueGroupCounts` is
 * single-dimension, so this fires it once per age status (an `age.status` `where`
 * filter, grouped by stage) and folds the three exact count maps into one stacked
 * row per stage — no client-side pagination, exact totals. Stages with no work in
 * scope are dropped.
 */
export function useStageThroughput(workflowId?: string | null) {
  const base = useMemo(
    () => ({ ...ARCHIVED_OUT, ...(workflowId ? { workflowId: { eq: workflowId } } : {}) }),
    [workflowId],
  );
  const { where } = useScopedWhere(base);
  const { stages } = useWorkspaceData();

  // One `where` per age status, AND-merged with the scoped predicate. Memoized so
  // each `useIssueGroupCounts` sees a stable variables object (no refetch churn).
  const wheres = useMemo(
    () => ISSUE_AGE_STATUSES.map((k) => ({ AND: [where, { age: { status: { eq: k } } }] })),
    [where],
  );

  const onTrack = useIssueGroupCounts({ where: wheres[0], groupBy: "stageId" });
  const atRisk = useIssueGroupCounts({ where: wheres[1], groupBy: "stageId" });
  const offTrack = useIssueGroupCounts({ where: wheres[2], groupBy: "stageId" });
  // The unfiltered per-stage total (no age filter). This is the true count a
  // stage should show — the three age buckets can sum to less than it when a
  // workspace has age tracking off or some issues carry no age status. Deriving
  // `untracked` from it keeps every populated stage on the chart (B5).
  const totals = useIssueGroupCounts({ where, groupBy: "stageId" });

  const loading = !onTrack.ready || !atRisk.ready || !offTrack.ready || !totals.ready;
  const error = onTrack.error ?? atRisk.error ?? offTrack.error ?? totals.error;

  const data = useMemo<ThroughputRow[]>(() => {
    const rows: ThroughputRow[] = [];
    for (const s of stages) {
      const total = totals.counts[s.stageId] ?? 0;
      // Gate removal on the TRUE total, not the sum of age buckets — otherwise a
      // workspace without age tracking renders an empty pipeline.
      if (total === 0) continue;
      const on = onTrack.counts[s.stageId] ?? 0;
      const risk = atRisk.counts[s.stageId] ?? 0;
      const off = offTrack.counts[s.stageId] ?? 0;
      const untracked = Math.max(0, total - (on + risk + off));
      rows.push({ id: s.stageId, label: s.name, onTrack: on, atRisk: risk, offTrack: off, untracked, total });
    }
    return rows;
  }, [onTrack.counts, atRisk.counts, offTrack.counts, totals.counts, stages]);

  return { data, loading, error };
}
